// AdminLayout.jsx
import { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/app/auth-context-state";
import api from "@/utils/axios";
import cls from "./AdminLayout.module.scss";
import CallOverlay from "@/components/Calls/CallOverlay";

// ✅ Icons (placeholders for now)
import homeIcon from "@/assets/UI/home.webp";
import menuIcon from "@/assets/UI/menu.webp";
import notificationsIcon from "@/assets/UI/notifications.webp";
import membersIcon from "@/assets/UI/friends.webp";
import memberCreateIcon from "@/assets/UI/createMember.webp";
import allCityIcon from "@/assets/UI/allCity.webp";
import createCityIcon from "@/assets/UI/createCity.webp";
import allClubIcon from "@/assets/UI/allClub.webp";
import allStreetIcon from "@/assets/UI/allStreet.webp";
import paymentOptionsIcon from "@/assets/UI/paymentOptions.webp";
import paymentSubmissionsIcon from "@/assets/UI/paymentSubmissions.webp";
import walletIcon from "@/assets/UI/wallet.webp";
import allAccountsIcon from "@/assets/UI/allAccounts.webp";
import exchangeIcon from "@/assets/UI/exchange.webp";
import promoIcon from "@/assets/UI/promo.webp";
import promoRIcon from "@/assets/UI/promoR.webp";
import chatIcon from "@/assets/UI/chat.webp";
import emailIcon from "@/assets/UI/email.webp";
// ✅ Central icon registry (easy to swap later)
const ICONS = {
  menu: menuIcon,
  home: homeIcon,
  notifications: notificationsIcon,
  members: membersIcon,
  memberCreate: memberCreateIcon,
  allCity: allCityIcon,
  createCity: createCityIcon,
  allClub: allClubIcon,
  allStreet: allStreetIcon,
  paymentOptions: paymentOptionsIcon,
  paymentSubmissions: paymentSubmissionsIcon,
  exchangeRates: exchangeIcon,
  wallets: walletIcon,
  walletTransactions: paymentOptionsIcon,
  ledgerEntries: paymentOptionsIcon,
  promos: promoIcon,
  promoRedemptions: promoRIcon,
  reports: paymentOptionsIcon,
  reconciliation: paymentSubmissionsIcon,
  accounts: allAccountsIcon,
  chat: chatIcon,
  email: emailIcon,
  pendingProducts: paymentSubmissionsIcon,
};

// ✅ Small helper component
const Icon = ({ name, alt = "", className }) => (
  <img src={ICONS[name]} alt={alt} className={className} draggable="false" />
);

export default function AdminLayout() {
  const [open, setOpen] = useState(true);
  const { user, setUser } = useAuth();
  const userName = user?.username || "Noty";

  const [totalMembers, setTotalMembers] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadChats, setUnreadChats] = useState(0);
  const [totalCities, setTotalCities] = useState(0);
  const [totalStreets, setTotalStreets] = useState(0);
  const [totalClubs, setTotalClubs] = useState(0);
  const [totalPendingProducts, setTotalPendingProducts] = useState(0);
  const [totalMarketplaceProducts, setTotalMarketplaceProducts] = useState(0);

  // Close sidebar with Escape
  const onKey = useCallback((e) => {
    if (e.key === "Escape") setOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onKey]);

  // Fetch quick stats (safe defaults if route missing)
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get("/v1/admin/layout/stats");
        const data = res.data || {};

        setTotalMembers(data.totalMembers ?? 0);
        setUnreadNotifications(data.unreadNotifications ?? 0);
        setUnreadChats(data.unreadChatCount ?? 0);
        setTotalCities(data.totalCities ?? 0);
        setTotalStreets(data.totalStreets ?? 0);
        setTotalClubs(data.totalClubs ?? 0);
        setTotalPendingProducts(data.totalPendingProducts ?? 0);
        setTotalMarketplaceProducts(data.totalMarketplaceProducts ?? 0);
      } catch (err) {
        console.error("Admin stats fetch failed:", err);
        setTotalMembers(0);
        setUnreadNotifications(0);
        setUnreadChats(0);
        setTotalCities(0);
        setTotalStreets(0);
        setTotalClubs(0);
        setTotalPendingProducts(0);
        setTotalMarketplaceProducts(0);
      }
    };

    fetchStats();
  }, []);

  const handleLogout = async () => {
    try {
      await api.post("/v1/auth/logout", {}, { withCredentials: true });
    } catch {
      // ignore
    } finally {
      localStorage.removeItem("cm_at");
      sessionStorage.removeItem("cm_at");

      if (api.defaults?.headers?.common?.Authorization) {
        delete api.defaults.headers.common.Authorization;
      }

      setUser(null);
      window.location.replace("/login");
    }
  };

  // Helper for NavLink class
  const navCls = ({ isActive }) =>
    `${cls.navLink} ${isActive ? cls.active : ""}`;

  return (
    <div
      className={`${cls.adminShell} ${open ? cls.isOpen : cls.isCollapsed}`}
      data-open={open ? "true" : "false"}
    >
      <div className={cls.bgGlow} aria-hidden="true" />

      {/* Sidebar */}
      <aside
        id="admin-sidebar"
        className={`${cls.sidebar} ${open ? cls.sidebarOpen : cls.sidebarClosed}`}
        aria-label="Admin navigation"
      >
        <div className={cls.sideTop}>
          <button
            type="button"
            className={cls.burger}
            aria-label="Toggle menu"
            aria-controls="admin-sidebar"
            aria-expanded={open ? "true" : "false"}
            onClick={() => setOpen((v) => !v)}
          >
            <span className={cls.burgerIconWrap}>
              <Icon name="menu" alt="menu" className={cls.burgerIcon} />
            </span>
          </button>

          <div className={cls.brand}>
            <strong className={cls.brandTitle}>Noty Admin</strong>
            <span className={cls.brandBadge}>Control Room</span>
          </div>
        </div>

        <nav className={cls.nav} aria-label="Admin primary">
          <ul className={cls.navList}>
            <li className={cls.navItem}>
              <NavLink to="/admin" end className={navCls}>
                <Icon name="home" alt="dashboard" className={cls.navIcon} />
                <span className={cls.navText}>Dashboard</span>
              </NavLink>
            </li>

            <li className={cls.groupLabel}>Members</li>

            <li className={cls.navItem}>
              <NavLink to="/admin/members" end className={navCls}>
                <Icon name="members" alt="members" className={cls.navIcon} />
                <span className={cls.navText}>New Members</span>
                <em className={cls.count}>{totalMembers}</em>
              </NavLink>
            </li>

            <li className={cls.navItem}>
              <NavLink to="/admin/members/create" end className={navCls}>
                <Icon
                  name="memberCreate"
                  alt="create member"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Create Member</span>
              </NavLink>
            </li>

            <li className={cls.groupLabel}>Marketplace</li>
            <li className={cls.navItem}>
              <NavLink
                to="/admin/marketplace/pending-products"
                end
                className={navCls}
              >
                <Icon
                  name="pendingProducts"
                  alt="pending products"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Pending Products</span>
                <em className={cls.count}>{totalPendingProducts}</em>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink
                to="/admin/marketplace/all-products"
                end
                className={navCls}
              >
                <Icon
                  name="pendingProducts"
                  alt="all products"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>All Products</span>
                <em className={cls.count}>{totalMarketplaceProducts}</em>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink to="/admin/chat" end className={navCls}>
                <Icon name="chat" alt="chat" className={cls.navIcon} />
                <span className={cls.navText}>Chat</span>
                <em className={cls.count}>{unreadChats}</em>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink to="/admin/emails" end className={navCls}>
                <Icon name="email" alt="email center" className={cls.navIcon} />
                <span className={cls.navText}>Email Center</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink to="/admin/chat/monitor" end className={navCls}>
                <Icon name="chat" alt="chat monitor" className={cls.navIcon} />
                <span className={cls.navText}>Message Monitor</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink to="/admin/friend-requests" end className={navCls}>
                <Icon
                  name="members"
                  alt="friend requests"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Friend Requests</span>
              </NavLink>
            </li>
            <li className={cls.groupLabel}>Game</li>
            <li className={cls.navItem}>
              <NavLink to="/admin/games" end className={navCls}>
                <Icon name="members" alt="games" className={cls.navIcon} />
                <span className={cls.navText}>Games</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink to="/admin/games/create" end className={navCls}>
                <Icon
                  name="memberCreate"
                  alt="create game"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Create Game</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink to="/admin/game-questions" end className={navCls}>
                <Icon
                  name="chat"
                  alt="game questions"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Game Questions</span>
              </NavLink>
            </li>

            <li className={cls.groupLabel}>Finance</li>
            <li className={cls.navItem}>
              <NavLink to="/admin/finance/accounts" end className={navCls}>
                <Icon name="accounts" alt="accounts" className={cls.navIcon} />
                <span className={cls.navText}>All Accounts</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink
                to="/admin/finance/payment-submissions"
                end
                className={navCls}
              >
                <Icon
                  name="paymentSubmissions"
                  alt="cities"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Payment Submissions</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink to="/admin/finance/wallets" end className={navCls}>
                <Icon name="wallets" alt="wallets" className={cls.navIcon} />
                <span className={cls.navText}>Wallets</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink
                to="/admin/finance/payment-options"
                end
                className={navCls}
              >
                <Icon
                  name="paymentOptions"
                  alt="cities"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Payment Options</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink
                to="/admin/finance/exchange-rates"
                end
                className={navCls}
              >
                <Icon
                  name="exchangeRates"
                  alt="exchange rates"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Exchange Rates</span>
              </NavLink>
            </li>

            <li className={cls.navItem}>
              <NavLink
                to="/admin/finance/ledger-entries"
                end
                className={navCls}
              >
                <Icon
                  name="ledgerEntries"
                  alt="ledger entries"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Ledger Entries</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink to="/admin/finance/promos" end className={navCls}>
                <Icon name="promos" alt="promos" className={cls.navIcon} />
                <span className={cls.navText}>Promo Codes</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink
                to="/admin/finance/promo-redemptions"
                end
                className={navCls}
              >
                <Icon
                  name="promoRedemptions"
                  alt="promo redemptions"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Promo Redemptions</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink to="/admin/finance/reports" end className={navCls}>
                <Icon name="reports" alt="reports" className={cls.navIcon} />
                <span className={cls.navText}>Reports</span>
              </NavLink>
            </li>
            <li className={cls.navItem}>
              <NavLink
                to="/admin/finance/reconciliation"
                end
                className={navCls}
              >
                <Icon
                  name="reconciliation"
                  alt="reconciliation"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Reconciliation</span>
              </NavLink>
            </li>
            <li className={cls.groupLabel}>Cities</li>

            <li className={cls.navItem}>
              <NavLink to="/admin/cities" end className={navCls}>
                <Icon name="allCity" alt="cities" className={cls.navIcon} />
                <span className={cls.navText}>All Cities</span>
                <em className={cls.count}>{totalCities}</em>
              </NavLink>
            </li>

            <li className={cls.navItem}>
              <NavLink to="/admin/cities/create" end className={navCls}>
                <Icon
                  name="createCity"
                  alt="create city"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Create A City</span>
              </NavLink>
            </li>

            <li className={cls.navItem}>
              <NavLink to="/admin/streets" end className={navCls}>
                <Icon name="allStreet" alt="streets" className={cls.navIcon} />
                <span className={cls.navText}>All Streets</span>
                <em className={cls.count}>{totalStreets}</em>
              </NavLink>
            </li>

            <li className={cls.navItem}>
              <NavLink to="/admin/clubs" end className={navCls}>
                <Icon name="allClub" alt="clubs" className={cls.navIcon} />
                <span className={cls.navText}>All Clubs</span>
                <em className={cls.count}>{totalClubs}</em>
              </NavLink>
            </li>

            <li className={cls.groupLabel}>Ops</li>

            <li className={cls.navItem}>
              <NavLink to="/admin/cms" className={navCls}>
                <Icon name="home" alt="cms" className={cls.navIcon} />
                <span className={cls.navText}>CMS</span>
              </NavLink>
            </li>

            <li className={cls.navItem}>
              <NavLink to="/admin/content" className={navCls}>
                <Icon
                  name="home"
                  alt="content moderation"
                  className={cls.navIcon}
                />
                <span className={cls.navText}>Content &amp; Moderation</span>
              </NavLink>
            </li>

            <li className={cls.navItem}>
              <NavLink to="/admin/events" className={navCls}>
                <Icon name="home" alt="events" className={cls.navIcon} />
                <span className={cls.navText}>Events &amp; Clubs</span>
              </NavLink>
            </li>

            <li className={cls.navItem}>
              <NavLink to="/admin/support" className={navCls}>
                <Icon name="home" alt="support" className={cls.navIcon} />
                <span className={cls.navText}>Support</span>
              </NavLink>
            </li>

            <li className={cls.navItem}>
              <NavLink to="/admin/settings" className={navCls}>
                <Icon name="home" alt="settings" className={cls.navIcon} />
                <span className={cls.navText}>Settings</span>
              </NavLink>
            </li>

            <li className={cls.navItem}>
              <button
                type="button"
                onClick={handleLogout}
                className={`${cls.navLink} ${cls.logoutBtn}`}
                aria-label="Log out"
              >
                <Icon name="home" alt="logout" className={cls.navIcon} />
                <span className={cls.navText}>Logout</span>
              </button>
            </li>
          </ul>
        </nav>

        <div className={cls.sideBottom}>
          <div className={cls.miniProfile}>
            <span className={cls.miniAvatarWrap}>
              <Icon name="home" alt="avatar" className={cls.miniAvatar} />
            </span>
            <div className={cls.miniProfileText}>
              <strong className={cls.miniName}>{userName}</strong>
              <small className={cls.miniRole}>Admin</small>
            </div>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className={cls.mainCol}>
        <header className={cls.topbar}>
          <div className={cls.topLeft}>
            <button
              type="button"
              className={cls.menuBtn}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-controls="admin-sidebar"
              aria-expanded={open ? "true" : "false"}
              onClick={() => setOpen((v) => !v)}
            >
              <span className={cls.burgerIconWrap}>
                <Icon name="menu" alt="menu" className={cls.burgerIcon} />
              </span>
            </button>

            <div className={cls.crumbTitle}>
              <span className={cls.title}>Admin Dashboard</span>
              <span className={cls.subtitle}>Control</span>
            </div>
          </div>

          <div className={cls.topRight}>
            <NavLink
              to="/admin/notifications"
              className={({ isActive }) =>
                `${cls.notifyBtn} ${isActive ? cls.notifyBtnActive : ""}`
              }
              aria-label="Notifications"
            >
              <Icon
                name="notifications"
                alt="notifications"
                className={cls.notifyIcon}
              />

              {unreadNotifications > 0 && (
                <span className={cls.notifyBadge}>
                  {unreadNotifications > 99 ? "99+" : unreadNotifications}
                </span>
              )}
            </NavLink>
            <NavLink
              to="/admin/chat"
              className={({ isActive }) =>
                `${cls.notifyBtn} ${isActive ? cls.notifyBtnActive : ""}`
              }
              aria-label="Chat"
            >
              <Icon name="chat" alt="chat" className={cls.notifyIcon} />

              {unreadChats > 0 && (
                <span className={cls.notifyBadge}>
                  {unreadChats > 99 ? "99+" : unreadChats}
                </span>
              )}
            </NavLink>
            <div className={cls.profilePill}>
              <span className={cls.avatarWrap}>
                <Icon name="home" alt="avatar" className={cls.avatar} />
              </span>
              <div className={cls.profileText}>
                <strong className={cls.profileName}>{userName}</strong>
                <small className={cls.profileMeta}>Admin</small>
              </div>
            </div>
          </div>
        </header>

        <main className={cls.outlet}>
          <Outlet />
        </main>
      </div>

      {/* Mobile overlay */}
      {open && (
        <button
          type="button"
          aria-label="Close sidebar"
          className={cls.overlay}
          onClick={() => setOpen(false)}
        />
      )}
      <CallOverlay />
    </div>
  );
}
